import React, { FC, useEffect, useRef } from 'react';
import { Spin, Tag, notification } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetNewsByIdQuery, useResetRetryMutation, useUpdateStatusMutation } from 'app/services/edpApiV1/news';
import { FrequencyDatesBack, FrequencyDatesToFront, NewsTabs, NewsTabsToRus } from 'shared/enums/news';
import { PICKER_DATE_MASK } from 'shared/constants';
import moment from 'moment';
import { IUpdateNewsRequest } from 'shared/types/news.types';
import { addNotifications } from 'shared/helpers/utils';
import style from './index.module.scss';
import ActionButtons from './actionButtons/ActionButtons';

const InfoNews: FC = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { data, isLoading: isGetByIdLoading, isFetching } = useGetNewsByIdQuery({ id: Number(id) });
  const newsData = useRef<HTMLDivElement>(null);
  const fromArchive = searchParams.get('fromArchive');
  const [updateNewsStatusQuery, { isLoading: isUpdateLoading }] = useUpdateStatusMutation();
  const [resetPublicationQuery, { isLoading: isResetLoading }] = useResetRetryMutation();
  const navigate = useNavigate();

  useEffect(() => {
    if (newsData.current && data) {
      newsData.current.innerHTML = data?.content;
    }
  }, [data]);

  const navigateAfterSuccess = () => {
    navigate('/news');
  };

  const unarchiveNews = async () => {
    if (!data) {
      notification.error({ message: 'Не удалось восстановить новость' });
      return;
    }
    await addNotifications(
      updateNewsStatusQuery({ id: Number(id), status: NewsTabs.UNPUBLICATED }),
      'Новость восстановлена',
      'Ошибка при восстановлении новости',
      navigateAfterSuccess,
    );
  };

  const archiveNews = async () => {
    if (!data) {
      notification.error({ message: 'Не удалось архивировать новость' });
      return;
    }
    await addNotifications(
      updateNewsStatusQuery({ id: Number(id), status: NewsTabs.ARCHIVE }),
      'Новость занесена в архив',
      'Ошибка при архивировании новости',
      navigateAfterSuccess,
    );
  };

  const resetPublication = async () => {
    await addNotifications(
      resetPublicationQuery({ id: Number(id) }),
      'Публикация новости сброшена',
      'Ошибка при сбросе публикации новости',
      navigateAfterSuccess,
    );
  };

  const prettyDate = (data: string | undefined) => {
    if (data) {
      return moment(data).format(PICKER_DATE_MASK);
    }
    return 'Отсутствует';
  };

  return (
    <Spin spinning={isGetByIdLoading || isFetching}>
      {data ? (
        <div className={style['news-info']}>
          <div className={style.top}>
            <h3>{data?.title}</h3>
            <ActionButtons
              archiveNews={archiveNews}
              fromArchive={fromArchive}
              unarchiveNews={unarchiveNews}
              resetPublication={resetPublication}
              isUpdateLoading={isUpdateLoading}
              isResetLoading={isResetLoading}
              data={data}
            />
          </div>
          <div ref={newsData} className={style.data}></div>
          <div className={style.info}>
            <p>Дата создания: {prettyDate(data?.created)}</p>
            <p>
              Автор: {data?.author.middleName} {data?.author.name} {data?.author.lastName}
            </p>
            {!fromArchive ? (
              <>
                <p>Дата последнего редактирования: {prettyDate(data.updateDate)}</p>
                <p>Дата публикации: {prettyDate(data.publicationDate)}</p>
                <p>Статус: {NewsTabsToRus[data?.status]}</p>
                <p>
                  Кому:{' '}
                  {data?.users?.length <= 0 && data?.organizations?.length <= 0 && data?.divisions?.length <= 0 ? (
                    <Tag key={-1} style={{ borderRadius: '10px' }}>
                      Всем сотрудникам
                    </Tag>
                  ) : (
                    <>
                      {data?.users.map(tag => (
                        <Tag key={tag.id} style={{ borderRadius: '10px' }}>
                          {tag.middleName} {tag.name} {tag.lastName}
                        </Tag>
                      ))}
                      {data?.organizations.map(tag => (
                        <Tag key={tag.id} style={{ borderRadius: '10px' }}>
                          {tag.name}
                        </Tag>
                      ))}
                      {data?.divisions.map(tag => (
                        <Tag key={tag.id} style={{ borderRadius: '10px' }}>
                          {tag.name}
                        </Tag>
                      ))}
                    </>
                  )}
                </p>
                <p>
                  Периодичность публикации:{' '}
                  {data?.retry !== FrequencyDatesBack.NONE ? FrequencyDatesToFront[data?.retry] : 'Не выбрано'}
                </p>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </Spin>
  );
};

export default InfoNews;
